import { ACCOUNT_STATUS } from '@community_dev/types/lib/api/Billing'
import { COMMUNITY_REGISTRATION_STATUS, CommunityRegistrationStatus } from '@community_dev/types/lib/api/v2/TCR'
import lowerCase from 'lodash/lowerCase'
import startCase from 'lodash/startCase'

import { Tone } from '../../colors'
import { isThrottlingActive } from '../Billing/components/Subscription/messageLimit'

import { ROUTES } from 'constants/routes'

export type AttentionTone = 'warning' | 'error'

export type AttentionItem = {
  id: string
  route: string
  tone: AttentionTone
  title: string
  description: string
  badge: string
  actionLabel: string
}

export type HealthSignals = {
  accountStatus?: string
  monthlyThreshold: number
  monthlyUsed: number
  provisioningErrors?: { numbers: number; campaigns: number }
  tcrStatus?: CommunityRegistrationStatus
}

export const ACCOUNT_STATUS_DISPLAY: Record<string, { label: string; tone: Tone }> = {
  [ACCOUNT_STATUS.ACTIVE]: { label: 'Active', tone: 'success' },
  [ACCOUNT_STATUS.INACTIVE]: { label: 'Inactive', tone: 'error' },
}

const FAILED_TCR_STATUSES: CommunityRegistrationStatus[] = [
  COMMUNITY_REGISTRATION_STATUS.REJECTED,
  COMMUNITY_REGISTRATION_STATUS.REVOKED,
]

export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count.toLocaleString('en-US')} ${count === 1 ? singular : plural}`
}

export function formatTcrStatus(status: string): string {
  return startCase(lowerCase(status))
}

export function getAttentionItems(signals: HealthSignals): AttentionItem[] {
  const items: AttentionItem[] = []

  if (signals.accountStatus === ACCOUNT_STATUS.INACTIVE) {
    items.push({
      id: 'account-inactive',
      route: ROUTES.CLIENT.BILLING,
      tone: 'error',
      title: 'Billing account is inactive',
      description: 'Check the account status and plan type in Billing.',
      badge: 'Inactive',
      actionLabel: 'Open Billing',
    })
  }

  const { numbers = 0, campaigns = 0 } = signals.provisioningErrors ?? {}
  const provisioningBadge = pluralize(numbers + campaigns, 'error')
  if (numbers > 0) {
    items.push({
      id: 'provisioning-numbers',
      route: ROUTES.CLIENT.NUMBER_PROVISIONING,
      tone: 'error',
      title: `${pluralize(numbers, 'phone number')} failed provisioning`,
      description: 'Check the failed provisioning checks in Number Provisioning.',
      badge: provisioningBadge,
      actionLabel: 'Open Number Provisioning',
    })
  }
  if (campaigns > 0) {
    items.push({
      id: 'provisioning-campaigns',
      route: ROUTES.CLIENT.NUMBER_PROVISIONING,
      tone: 'error',
      title: `${pluralize(campaigns, 'TCR campaign')} failed provisioning`,
      description: 'Check the campaign links in Number Provisioning.',
      badge: provisioningBadge,
      actionLabel: 'Open Number Provisioning',
    })
  }

  if (signals.tcrStatus && FAILED_TCR_STATUSES.includes(signals.tcrStatus)) {
    const status = formatTcrStatus(signals.tcrStatus)
    items.push({
      id: 'tcr-registration',
      route: ROUTES.CLIENT.TCR,
      tone: 'error',
      title: `TCR registration is ${status.toLowerCase()}`,
      description: 'Check the brand and campaign registration in TCR.',
      badge: status,
      actionLabel: 'Open TCR',
    })
  }

  if (isThrottlingActive(signals.monthlyThreshold) && signals.monthlyUsed >= signals.monthlyThreshold) {
    items.push({
      id: 'monthly-limit-reached',
      route: ROUTES.CLIENT.BILLING,
      tone: 'warning',
      title: 'Monthly message limit reached',
      description: `${signals.monthlyUsed.toLocaleString('en-US')} of ${signals.monthlyThreshold.toLocaleString(
        'en-US',
      )} campaign messages are used this month.`,
      badge: 'Limit reached',
      actionLabel: 'Open Billing',
    })
  }

  return items
}

// A section can have more than one item. The nav shows the badge of the first item, and errors come first.
export function getSectionBadges(items: AttentionItem[]): Record<string, Pick<AttentionItem, 'badge' | 'tone'>> {
  const badges: Record<string, Pick<AttentionItem, 'badge' | 'tone'>> = {}
  for (const { route, badge, tone } of items) {
    if (!badges[route]) badges[route] = { badge, tone }
  }
  return badges
}
